/**
 * What each Chart Creator workbook was built from, remembered for as long as
 * the task pane is open.
 *
 * An executor run lasts one user turn. Keeping this on the run's session state
 * meant that by the time the user said "add Netflix", the parameters the chart
 * was created from were already gone — so an edit that could not be written to
 * worksheet cells had nothing real to re-request and had to give up. The
 * records live here instead, keyed by workbook name, so a later turn can still
 * regenerate the exact chart with only the requested fields changed.
 */

import type { FactsetChartParameters } from "./types";

/**
 * What a native-chart workbook was built from, for later edits. Summary-table
 * choices live in `parameters` like every other request field, so a rebuild
 * cannot send one set of settings while a reader reports another.
 */
export interface NativeChartWorkbookRecord {
  parameters: FactsetChartParameters;
  sheetNames: string[];
  createdAt: number;
}

export interface NativeChartWorkbookRecordStore {
  get(workbookName: string): NativeChartWorkbookRecord | undefined;
  set(workbookName: string, record: NativeChartWorkbookRecord): void;
}

/** More native-chart workbooks than one Excel session realistically juggles. */
const MAX_REMEMBERED_WORKBOOKS = 12;

export function createNativeChartWorkbookRecordStore(): NativeChartWorkbookRecordStore {
  const records = new Map<string, NativeChartWorkbookRecord>();
  const key = (workbookName: string): string =>
    workbookName.trim().toLowerCase();
  return {
    get: (workbookName) => records.get(key(workbookName)),
    set: (workbookName, record) => {
      const id = key(workbookName);
      // Re-insert so iteration order stays oldest-first and eviction drops the
      // workbook nobody has touched in the longest time.
      records.delete(id);
      records.set(id, record);
      while (records.size > MAX_REMEMBERED_WORKBOOKS) {
        const oldest = records.keys().next();
        if (oldest.done) break;
        records.delete(oldest.value);
      }
    },
  };
}

/**
 * Shared store. Production code uses this singleton so records survive between
 * executor runs; tests inject an isolated store instead.
 */
export const nativeChartWorkbookRecords: NativeChartWorkbookRecordStore =
  createNativeChartWorkbookRecordStore();
